// Browser-side preprocessing for image attachments. Phone camera photos often
// exceed the server's 4 MB request cap, so bound the longest edge and JPEG size
// before the attachment adapter turns the file into a message part.

export const IMAGE_ATTACHMENT_TARGET_BYTES = 3.5 * 1024 * 1024;
export const IMAGE_ATTACHMENT_MAX_EDGE = 1568;

type CanvasLike = {
  width: number;
  height: number;
  getContext: (type: '2d') => Pick<CanvasRenderingContext2D, 'drawImage'> | null;
  toBlob: (callback: (blob: Blob | null) => void, type: string, quality: number) => void;
};

export type ImagePreprocessingDependencies = {
  createBitmap: (file: File) => Promise<ImageBitmap>;
  createCanvas: () => CanvasLike;
};

const browserDependencies: ImagePreprocessingDependencies = {
  createBitmap: (file) => createImageBitmap(file),
  createCanvas: () => document.createElement('canvas'),
};

function jpegName(name: string) {
  return /\.[^./]+$/u.test(name) ? name.replace(/\.[^./]+$/u, '.jpg') : `${name}.jpg`;
}

const preparationMessage = 'This image could not be prepared. Remove it and choose another image.';
const supportedRasterTypes = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);

function assertDimensions(bitmap: ImageBitmap) {
  if (![bitmap.width, bitmap.height].every((edge) => Number.isSafeInteger(edge) && edge > 0))
    throw new Error(preparationMessage);
}

function alreadyBounded(file: File, bitmap: ImageBitmap) {
  return (
    supportedRasterTypes.has(file.type) &&
    file.size <= IMAGE_ATTACHMENT_TARGET_BYTES &&
    Math.max(bitmap.width, bitmap.height) <= IMAGE_ATTACHMENT_MAX_EDGE
  );
}

function canvasBlob(canvas: CanvasLike, quality: number) {
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

export async function downscaleImage(
  file: File,
  dependencies: ImagePreprocessingDependencies = browserDependencies,
): Promise<File> {
  if (!file.type.startsWith('image/')) return file;

  let bitmap: ImageBitmap | undefined;
  try {
    bitmap = await dependencies.createBitmap(file);
    assertDimensions(bitmap);
    if (alreadyBounded(file, bitmap)) return file;
    const scale = Math.min(1, IMAGE_ATTACHMENT_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = dependencies.createCanvas();
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error(preparationMessage);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    for (const quality of [0.85, 0.7, 0.55]) {
      const blob = await canvasBlob(canvas, quality);
      if (blob && blob.type === 'image/jpeg' && blob.size <= IMAGE_ATTACHMENT_TARGET_BYTES) {
        return new File([blob], jpegName(file.name), { type: 'image/jpeg' });
      }
    }
    throw new Error(preparationMessage);
  } catch (cause) {
    throw new Error(preparationMessage, { cause });
  } finally {
    bitmap?.close();
  }
}
